import React from "react";
import ReactPlayer from "react-player";
import styled, { css } from "styled-components";

import { PanelType } from "../../contexts/PanelContext";
import {
  NodeEvent,
  NodeHeritageSite,
  NodeSayings,
  ParagraphHeroImage,
  ParagraphHeroVideo,
} from "../../generated/graphql";
import { Icons } from "../../utils/icons";
import { Theme } from "../../utils/theme";
import { LinkButton } from "../shared/Button";
import CircleIcon from "../shared/CircleIcon";
import Icon from "../shared/Icon";
import Text from "../shared/Text";
import CloseButton from "./CloseButton";
import { InnerWrapper } from "./InnerWrapper";
import ParagraphWrapper from "./ParagraphWrapper";
import RelatedEvent from "./RelatedEvent";
import EventDateLocation from "./EventDateLocation";

interface PanelContentProps {
  type: PanelType;
  data: NodeHeritageSite & NodeEvent & NodeSayings;
  associatedEvents?: NodeEvent[];
}

interface Tab {
  name: string;
  icon: keyof Icons;
}

const heritageSiteTab: Tab = {
  name: "info",
  icon: "site",
};

const PanelContentWrapper: React.FC<PanelContentProps> = (props) => {
  const [tabs, setTabs] = React.useState<Tab[]>([heritageSiteTab]);

  React.useEffect(() => {
    if (props.associatedEvents?.length) {
      setTabs([
        heritageSiteTab,
        {
          name: "events",
          icon: "event",
        },
      ]);
    } else {
      setTabs([heritageSiteTab]);
    }
  }, [props.associatedEvents]);

  React.useEffect(() => {
    setSelectedTab(tabs[0]);
  }, [props.data, tabs]);
  const [selectedTab, setSelectedTab] = React.useState(tabs[0]);
  const heroImage =
    (props.data?.fieldHero?.entity as ParagraphHeroImage) || undefined;
  const heroVideo =
    (props.data?.fieldHero?.entity as ParagraphHeroVideo) || undefined;

  const pageContent = props.data?.fieldContent;

  const getIconType = (
    typename?: string
  ): { icon: keyof Icons; color: keyof Theme["colors"] } => {
    if (typename === "NodeHeritageSite") {
      return {
        icon: "site",
        color: "darkRed",
      };
    }
    if (typename === "NodeEvent") {
      return {
        icon: "event",
        color: "darkGreen",
      };
    } else {
      return {
        icon: "story",
        color: "darkBlue",
      };
    }
  };
  return (
    <Container>
      <PanelHeader>
        <InnerWrapper>
          {heroImage?.fieldHeroImage?.url && (
            <HeaderMedia>
              <img
                src={heroImage.fieldHeroImage.url}
                alt={heroImage.fieldHeroImage.alt || ""}
              />
            </HeaderMedia>
          )}
          {heroVideo?.fieldHeroVideoUrl && (
            <ResponsivePlayer>
              <ReactPlayer
                className="react-player"
                url={heroVideo?.fieldHeroVideoUrl}
                width="100%"
                height="100%"
              />
            </ResponsivePlayer>
          )}
          {props.type === "poi" && (
            <HeaderTitle>
              <CircleIcon
                icon={getIconType(props.data?.__typename).icon}
                color={getIconType(props.data?.__typename).color}
              />

              <Title>
                <Text as="h1">
                  {props.data?.fieldSiteName || props.data?.title}
                </Text>
                <Text as="h3">{props.data?.fieldLocation}</Text>
                {props.data?.__typename === "NodeEvent" && (
                  <EventDateLocation event={props.data} />
                )}
              </Title>
              {props.data?.fieldSiteLink?.uri && (
                <HeaderLink>
                  <LinkButton
                    small={true}
                    target="_blank"
                    href={props.data?.fieldSiteLink.uri}
                  >
                    {props.data?.fieldSiteLink?.title || "Visit"}
                  </LinkButton>
                </HeaderLink>
              )}
            </HeaderTitle>
          )}
        </InnerWrapper>
      </PanelHeader>
      {props.type === "poi" && (
        <PanelTabsWrapper>
          <InnerWrapper>
            {tabs.length > 1 && (
              <TabsHeader>
                {tabs.map((tab, i) => {
                  return (
                    <TabButton
                      className={selectedTab.name === tab.name ? "active" : ""}
                      onClick={() => setSelectedTab(tab)}
                      key={i}
                    >
                      <Icon icon={tab.icon} />
                      {tab.name}
                    </TabButton>
                  );
                })}
              </TabsHeader>
            )}
            <TabWrapper>
              {selectedTab.name === "info" && (
                <TabContent>
                  <ParagraphWrapper pageContent={pageContent} />
                </TabContent>
              )}
              {selectedTab.name === "events" && (
                <TabContent>
                  {props.associatedEvents?.map((event, i) => {
                    return <RelatedEvent key={i} event={event} />;
                  })}
                </TabContent>
              )}
            </TabWrapper>
            <CloseButton />
          </InnerWrapper>
        </PanelTabsWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  /* padding: 1rem; */
`;

const PanelHeader = styled.div`
  padding-bottom: 2rem;
`;
const PanelTabsWrapper = styled.div`
  ${({ theme }) => css`
    flex-grow: 1;
    background-color: ${theme.colors.bgAccent};
    padding: 1.5rem 0;
  `}
`;
const TabsHeader = styled.div`
  display: flex;
  padding-bottom: 1.5rem;
`;
const TabButton = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    font-weight: 700;
    font-size: 0.8rem;
    margin-right: 1.2rem;
    padding-bottom: 0.4rem;
    cursor: pointer;
    transition: all ease-in-out 0.2s;
    border-bottom: 2px solid transparent;

    &.active,
    &:hover,
    &:focus {
      color: ${theme.colors.accent};
      border-bottom: 2px solid ${theme.colors.accent};
      svg {
        fill: ${theme.colors.accent};
        padding-right: 0.5rem;
      }
    }
    svg {
      transition: all ease-in-out 0.2s;
      padding-right: 0.5rem;
    }
  `}
`;
const TabWrapper = styled.div``;
const TabContent = styled.div``;

const HeaderMedia = styled.div`
  width: 100%;
  height: 540px;
  display: flex;
  img {
    width: 100%;
    object-fit: cover;
  }
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  flex-direction: row;
  width: 100%;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 1rem;

  h1 {
    margin: 0;
    padding: 0.4rem 0;
  }
  h3 {
    padding: 0;
    margin: 0;
  }
`;

const HeaderLink = styled.div`
  margin-left: auto;
`;

const ResponsivePlayer = styled.div`
  position: relative;
  padding-top: 56.25%; /* Player ratio: 100 / (1280 / 720) */

  .react-player {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

export default PanelContentWrapper;
